///
/// EarthBrowser
///
/// Created by Matt Giger on 9/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { QuakeShockwaveNode } from "@/earthbrowser/dataset-distribution/earthquakes/QuakeShockwaveNode"
import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { EmberDomeNode } from "@/earthbrowser/entities/EmberDome/EmberDomeNode"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { requestRender } from "@stovokor/engine/RenderRequest"
import { animation, easing } from "@stovokor/math/animation"
import { lerpColor, rgba } from "@stovokor/math/color"
import { matrix } from "@stovokor/math/matrix"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Marker radius scales exponentially with magnitude, mirroring quake energy:
// M2.5 ~ 1.5 km, M5 ~ 3 km, M6 ~ 6 km, M7 ~ 12 km, M8+ ~ 25 km
const cMinRadius = 1.5
const cMaxRadius = 25

// Recency windows (hours), measured against the simulation clock
const cBreakingHours = 2	// "breaking news": pulse + shockwave (only animated state)
const cRecentHours = 24		// slightly larger size floor, but static

// Clock-driven fade-out: a quake appears at its event time and fades to
// nothing over a lifetime that doubles per magnitude step, echoing the
// energy scaling used for the radius. M4 ~ 12h, M5 ~ 24h, M6 ~ 48h,
// M7 ~ 4 days, M8+ ~ 10 days (clamped)
const cFadeBaseHours = 12
const cFadeMinHours = 6
const cFadeMaxHours = 240
// Fraction of the lifetime held at full opacity before the fade begins
const cFadeHoldFraction = 0.25

// Minimum apparent marker radius in radians, so quakes stay visible from
// orbit. At a 45 degree fov on a 1080px-tall screen, 0.001 rad is ~1.3px.
// Breaking/alert quakes read as a clear pulsing dot at full-globe zoom;
// old minor quakes stay a barely-there fleck that rewards zooming in.
const cMinAngularBreaking = 0.0045	// ~6px radius on a 1080p screen
const cMinAngularRecent = 0.00225	// ~3px radius
const cMinAngularStatic = 0.0009	// ~1.2px radius

// Face-on solidity of the dome (0 = rim-only fresnel, 1 = solid ball).
// Solid enough to read clearly over bright terrain at mid/close zoom;
// red-800 with an airy rim-only look vanishes over land.
const cQuakeFill = 0.5
// Hovered: solid and brightened so the quake a tap would pick stands out
// of a dense cluster
const cQuakeFillHovered = 1
const cHoverBrighten = 0.35

export interface QuakeMarkerStyle {
	mag: number
	eventTimeMs: number
	alert: string | null
	tsunami: boolean
}

/** Dome radius in km for a given magnitude */
export function quakeMarkerRadius(mag: number): number {
	const radius = cMinRadius * Math.pow(2, mag - 4)
	return Math.min(cMaxRadius, Math.max(cMinRadius, radius))
}

/** Visible lifetime in hours for a given magnitude */
export function quakeLifetimeHours(mag: number): number {
	const hours = cFadeBaseHours * Math.pow(2, mag - 4)
	return Math.min(cFadeMaxHours, Math.max(cFadeMinHours, hours))
}

/**
 * Clock-driven opacity (0..1) of a quake ageMs after its event time.
 * 0 before the event (scrubbing back hides it), full opacity for the
 * first quarter of its lifetime, then an ease-out fade to nothing.
 */
export function quakeTimeFade(mag: number, ageMs: number): number {
	if(ageMs < 0) {
		return 0
	}
	const lifetimeMs = quakeLifetimeHours(mag) * 3.6e6
	const holdMs = lifetimeMs * cFadeHoldFraction
	if(ageMs <= holdMs) {
		return 1
	}
	const t = (ageMs - holdMs) / (lifetimeMs - holdMs)
	return t >= 1 ? 0 : 1 - t * t
}

/**
 * Composite earthquake marker:
 * - An ember dome (fresnel rim-glow sphere) sized to the magnitude and to
 *   the hit test radius, so big quakes are visible (and clickable) from far
 * - A seismic shockwave shell that ripples outward while the quake is breaking news
 *
 * All quakes use the single design-system red (red-800); magnitude is
 * communicated by size, recency by animation. Motion strictly means
 * "breaking news": pulse + shockwave only under 2 hours old (or while a
 * USGS orange/red alert or tsunami flag is active); everything else is
 * completely static so the globe stays calm.
 *
 * Recency and fade follow the simulation clock (updateClock/setTimeFade,
 * driven per frame by QuakeFeature), so scrubbing the timeline replays
 * a quake's appearance, breaking pulse and magnitude-scaled fade-out.
 */
export class QuakeMarkerNode extends NodeBase {
	private readonly markerContext: StovokorContext
	private readonly dome: EmberDomeNode
	private shockwave?: QuakeShockwaveNode
	private focusAnim?: animation
	private focusBlend = 0
	private focused = false
	private hovered = false
	private shockwaveActive = false
	private style: QuakeMarkerStyle
	private timeFade = 1
	private breaking = false
	private recent = false
	private clockStateApplied = false

	public radius: number
	/** Current minimum apparent size (radians); used for hit test inflation too */
	public minAngularSize = 0

	constructor(context: StovokorContext, style: QuakeMarkerStyle) {
		super()
		this.markerContext = context
		this.style = style
		this.radius = quakeMarkerRadius(style.mag)

		this.dome = new EmberDomeNode(context)
		this.dome.fillAlpha = cQuakeFill
		this.addNode(this.dome)

		this.updateStyle(style)
	}

	/** Recompute size and color. Called on creation and on every dataset
	 *  poll; recency animation is applied by updateClock each frame. */
	public updateStyle(style: QuakeMarkerStyle): void {
		this.style = style
		this.radius = quakeMarkerRadius(style.mag)
		this.dome.transform = matrix.scale(this.radius)
		this.applyHoverLook()
		// Force the next updateClock to re-apply the recency state
		this.clockStateApplied = false
	}

	/** Hover cue: brighten and solidify the dome a tap would pick */
	public setHovered(hovered: boolean): void {
		if(this.hovered === hovered) {
			return
		}
		this.hovered = hovered
		this.applyHoverLook()
		requestRender()
	}

	private applyHoverLook(): void {
		const base = DesignColors.MARKER_QUAKE
		this.dome.color = this.hovered
			? lerpColor(base, rgba(1, 1, 1, 1), cHoverBrighten)
			: base
		this.dome.fillAlpha = this.hovered ? cQuakeFillHovered : cQuakeFill
	}

	/** Advance the recency animation to the simulation clock. Called every
	 *  frame; only re-applies node state when the recency bucket changes. */
	public updateClock(clockMs: number): void {
		const ageHours = (clockMs - this.style.eventTimeMs) / 3.6e6
		const alertActive = this.style.alert === "orange" || this.style.alert === "red" || this.style.tsunami

		// Animation strictly means "breaking news": most of the feed is less
		// than a day old, so anything broader would leave the globe crawling
		const breaking = (ageHours >= 0 && ageHours < cBreakingHours) || alertActive

		// Screen-space size floor so quake activity is visible at global zoom:
		// breaking/alert events read as a clear pulsing dot from orbit
		const recent = ageHours < cRecentHours || this.style.mag >= 6

		if(this.clockStateApplied && breaking === this.breaking && recent === this.recent) {
			return
		}
		this.clockStateApplied = true
		this.breaking = breaking
		this.recent = recent

		const markerColor = DesignColors.MARKER_QUAKE
		this.dome.pulse = breaking ? 1 : 0
		this.minAngularSize = breaking ? cMinAngularBreaking
			: (recent ? cMinAngularRecent : cMinAngularStatic)
		this.dome.minAngularSize = this.minAngularSize

		// Seismic ripple only while the event is breaking news
		this.shockwaveActive = breaking
		if(this.shockwaveActive && !this.shockwave) {
			this.shockwave = new QuakeShockwaveNode(this.markerContext, markerColor)
			this.addNode(this.shockwave)
		}
		if(this.shockwave) {
			this.shockwave.color = markerColor
			this.shockwave.transform = matrix.scale(this.radius)
			this.shockwave.minAngularSize = this.minAngularSize
			this.applyOpacity()
		}
	}

	/** Clock-driven fade (0..1); multiplied with the focus fade */
	public setTimeFade(value: number): void {
		if(value === this.timeFade) {
			return
		}
		this.timeFade = value
		this.applyOpacity()
	}

	/** Fade the marker down while focused so it doesn't obscure the terrain */
	public setFocused(focused: boolean): void {
		if(this.focused === focused) {
			return
		}
		this.focused = focused

		this.focusAnim?.stop()
		this.focusAnim = new animation()
			.from(this.focusBlend)
			.to(focused ? 1 : 0)
			.duration(0.5)
			.easing(easing.quadraticInOut)
			.onUpdate(value => this.applyBlend(value))
		this.focusAnim.start()
	}

	private applyBlend(value: number): void {
		this.focusBlend = value
		this.applyOpacity()
	}

	private applyOpacity(): void {
		// Keep a faint dome while focused as a hint of the clickable volume
		this.dome.opacity = this.timeFade * (1 - 0.75 * this.focusBlend)
		if(this.shockwave) {
			this.shockwave.opacity = this.timeFade * (1 - this.focusBlend)
			this.shockwave.visible = this.shockwaveActive && this.focusBlend < 0.99
		}
	}

	override destroy(): void {
		this.focusAnim?.stop()
		super.destroy()
	}
}
